import {
  Alert,
  Badge,
  Breadcrumbs,
  Button,
  Card,
  Group,
  Loader,
  Progress,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { IconArrowNarrowLeft } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link as RALink, useTranslate } from 'react-admin'

import { Card as MUICard, CardContent } from '@mui/material'
import { useGetApiUsers } from '../../codegen/generated/users'
import { LXTable, LXTableColumn } from '../../components/LXTable/LXTable'
import { DateHelper, LONG_FORMAT } from '../../helpers/Date.helper'
import { NumberFormatter } from '../../helpers/NumberFormatter.helper'
import { FileCopyService } from '../../services/FileCopy.service'
import {
  FileCopyOperationFile,
  FileCopyOperationFileStatus,
  FileCopyOperationStatus,
} from '../../types/FileCopy'
import styles from './FileCopy.module.scss'

type Props = {
  operationId: string
}

const STATUS_COLORS: Record<FileCopyOperationStatus, string> = {
  pending: 'gray',
  running: 'blue',
  completed: 'green',
  failed: 'red',
  cancelled: 'gray',
}

const FILE_STATUS_COLORS: Record<FileCopyOperationFileStatus, string> = {
  pending: 'gray',
  success: 'green',
  failed: 'red',
  skipped: 'yellow',
  cancelled: 'gray',
}

type FileCopyOperationFileRow = {
  id: string
  file: JSX.Element
  statusItem: JSX.Element
  progress: JSX.Element
  error: JSX.Element
}

const isActive = (status: FileCopyOperationStatus): boolean =>
  status === 'pending' || status === 'running'

export const FileCopyOperationDetail = ({ operationId }: Props): JSX.Element => {
  const t = useTranslate()
  const queryClient = useQueryClient()
  const [fileStatusFilter, setFileStatusFilter] = useState<FileCopyOperationFileStatus | null>(null)
  const [search, setSearch] = useState('')
  const usersQuery = useGetApiUsers()

  const operationQuery = useQuery({
    queryKey: ['fileCopy', 'operation', operationId, fileStatusFilter, search],
    queryFn: () =>
      FileCopyService.getOperation(operationId, {
        fileStatus: fileStatusFilter ?? undefined,
        search: search.trim().length > 0 ? search.trim() : undefined,
      }),
    refetchInterval: data => (isActive(data?.operation.status ?? 'pending') ? 1000 : false),
  })

  const retryMutation = useMutation({
    mutationFn: (fileIds?: string[]) => FileCopyService.retryFailures(operationId, fileIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fileCopy', 'operation', operationId] })
      queryClient.invalidateQueries({ queryKey: ['fileCopy', 'operations'] })
    },
  })

  const cancelMutation = useMutation({
    mutationFn: () => FileCopyService.cancelOperation(operationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fileCopy', 'operation', operationId] })
      queryClient.invalidateQueries({ queryKey: ['fileCopy', 'operations'] })
    },
  })

  const operation = operationQuery.data?.operation

  const createdByEmail = useMemo(() => {
    if (!operation?.createdByUserId) return null
    const user = (usersQuery.data?.body ?? []).find(u => u.id === operation.createdByUserId)
    return user?.email ?? operation.createdByUserId
  }, [usersQuery.data, operation?.createdByUserId])
  const files = useMemo(() => operationQuery.data?.files ?? [], [operationQuery.data])
  const failedFileIds = useMemo(
    () => files.filter(file => file.status === 'failed').map(file => file.id),
    [files],
  )

  const statusFilters: FileCopyOperationFileStatus[] = [
    'success',
    'pending',
    'failed',
    'skipped',
    'cancelled',
  ]

  if (operationQuery.isLoading) {
    return (
      <Group justify="center" py="xl">
        <Loader />
      </Group>
    )
  }

  if (operationQuery.isError || !operation) {
    return (
      <Stack gap="md">
        <RALink to="/file-copy" style={{ alignSelf: 'flex-start' }}>
          <Group gap={4} wrap="nowrap">
            <IconArrowNarrowLeft size={16} />
            <Text size="sm">{t('pages.fileCopy.detail.back')}</Text>
          </Group>
        </RALink>
        <Alert color="red" title={t('pages.fileCopy.detail.error')}>
          {t('pages.fileCopy.detail.loadError', { error: String(operationQuery.error) })}
        </Alert>
      </Stack>
    )
  }

  const progress =
    operation.bytesTotal > 0
      ? Math.round((operation.bytesDone / operation.bytesTotal) * 100)
      : operation.filesCount > 0
        ? Math.round(
            ((operation.successCount + operation.failureCount + operation.cancelledCount) /
              operation.filesCount) *
              100,
          )
        : 0

  const fileItems: FileCopyOperationFileRow[] = files.map((file: FileCopyOperationFile) => ({
    id: file.id,
    file: (
      <Stack gap={0}>
        <Text size="sm">{file.fileName}</Text>
        <Text size="xs" c="dimmed">
          {[
            NumberFormatter.formatDigitalByte(file.sizeBytes ?? 0),
            file.resultDetail?.durationMs !== undefined &&
              t('pages.fileCopy.detail.fileDuration', {
                duration: DateHelper.getConvertedTime(file.resultDetail.durationMs, 'ms', true),
              }),
            file.status !== 'pending' &&
              file.processedAt &&
              t('pages.fileCopy.detail.fileFinishedAt', {
                date: DateHelper.format(file.processedAt, LONG_FORMAT),
              }),
          ]
            .filter(Boolean)
            .join(' · ')}
        </Text>
      </Stack>
    ),
    statusItem: (
      <Badge color={FILE_STATUS_COLORS[file.status]} variant="light">
        {t(`pages.fileCopy.fileStatuses.${file.status}`)}
      </Badge>
    ),
    progress:
      file.status === 'success' ? (
        <Progress value={100} size="sm" color="green" />
      ) : (
        <Progress
          value={
            (file.sizeBytes ?? 0) > 0
              ? Math.round((file.bytesTransferred / (file.sizeBytes ?? 0)) * 100)
              : 0
          }
          size="sm"
          color={file.status === 'failed' ? 'red' : 'blue'}
          animated={isActive(operation.status)}
        />
      ),
    error: file.errorMessage ? (
      <Text size="xs" c="red" lineClamp={2}>
        {file.errorMessage}
      </Text>
    ) : (
      <Text size="xs" c="dimmed">
        —
      </Text>
    ),
  }))

  const fileColumns: LXTableColumn<FileCopyOperationFileRow>[] = [
    { key: 'file', value: t('pages.fileCopy.detail.headers.file'), type: 'component' },
    { key: 'statusItem', value: t('pages.fileCopy.detail.headers.status'), type: 'component' },
    { key: 'progress', value: t('pages.fileCopy.detail.headers.progress'), type: 'component' },
    { key: 'error', value: t('pages.fileCopy.detail.headers.error'), type: 'component' },
  ]

  return (
    <MUICard>
      <CardContent className={styles.container}>
        <Stack gap="md">
          <Breadcrumbs>
            <RALink to="/file-copy">
              <Text size="sm" c="blue">
                {t('pages.fileCopy.detail.breadcrumb')}
              </Text>
            </RALink>
            <Text size="sm" c="dimmed">
              {operation.id}
            </Text>
          </Breadcrumbs>

          <Group justify="space-between" align="center">
            <Group gap="sm" align="center">
              <Title order={3}>{t('pages.fileCopy.detail.copy', { id: operation.id })}</Title>
              <Badge color={STATUS_COLORS[operation.status]} variant="light">
                {t(`pages.fileCopy.statuses.${operation.status}`)}
              </Badge>
            </Group>
            <Group gap="xs">
              {isActive(operation.status) && (
                <Button
                  size="xs"
                  color="red"
                  variant="subtle"
                  onClick={() => cancelMutation.mutate()}
                  loading={cancelMutation.isLoading}
                >
                  {t('pages.fileCopy.detail.cancel')}
                </Button>
              )}
              {operation.failureCount > 0 && !isActive(operation.status) && (
                <Button
                  size="xs"
                  variant="light"
                  onClick={() =>
                    retryMutation.mutate(failedFileIds.length ? failedFileIds : undefined)
                  }
                  loading={retryMutation.isLoading}
                >
                  {t('pages.fileCopy.detail.retryFailures', { count: operation.failureCount })}
                </Button>
              )}
            </Group>
          </Group>

          {cancelMutation.isError && (
            <Alert color="red" title={t('pages.fileCopy.detail.error')}>
              {t('pages.fileCopy.detail.cancelError', { error: String(cancelMutation.error) })}
            </Alert>
          )}
          {retryMutation.isError && (
            <Alert color="red" title={t('pages.fileCopy.detail.error')}>
              {t('pages.fileCopy.detail.retryError', { error: String(retryMutation.error) })}
            </Alert>
          )}

          <Stack gap="sm">
            <Progress
              value={progress}
              size="lg"
              animated={isActive(operation.status)}
              color={progress === 100 ? 'green' : undefined}
            />
            <Group justify="space-between">
              <Text size="sm" fw={500}>
                {t('pages.fileCopy.detail.progressSummary', {
                  progress,
                  done: NumberFormatter.formatDigitalByte(operation.bytesDone),
                  total: NumberFormatter.formatDigitalByte(operation.bytesTotal),
                })}
              </Text>
              <Group gap="md">
                <Text size="xs" c="dimmed">
                  {operation.successCount} {t('pages.fileCopy.detail.copied')}
                </Text>
                <Text size="xs" c={operation.failureCount > 0 ? 'red' : 'dimmed'}>
                  {operation.failureCount} {t('pages.fileCopy.detail.failed')}
                </Text>
                <Text size="xs" c="dimmed">
                  {operation.skippedCount ?? 0} {t('pages.fileCopy.detail.skipped')}
                </Text>
                <Text size="xs" c="dimmed">
                  {operation.cancelledCount ?? 0} {t('pages.fileCopy.detail.cancelled')}
                </Text>
              </Group>
            </Group>
            <Stack gap={3} mt="sm">
              {isActive(operation.status) && operation.currentFileName && (
                <Text size="xs" c="dimmed" style={{ fontStyle: 'italic' }}>
                  {t('pages.fileCopy.detail.currentFile', { name: operation.currentFileName })}
                </Text>
              )}
              {createdByEmail && (
                <Group>
                  <Text size="sm" c="dimmed">
                    {t('pages.fileCopy.detail.createdBy')}
                  </Text>
                  <Text size="sm">{createdByEmail}</Text>
                </Group>
              )}
              <Group>
                <Text size="sm" c="dimmed">
                  {t('pages.fileCopy.detail.destination')}
                </Text>
                <Text span size="sm">
                  gs://{operation.config.destination.bucketName}
                  {operation.config.destination.subFolderPrefix}
                </Text>
              </Group>
            </Stack>
          </Stack>

          <Stack gap="sm" mt="xl">
            <Group justify="space-between" align="flex-start">
              <Stack gap="xs">
                <Title order={5}>
                  {t('pages.fileCopy.detail.filesCount', {
                    count: operationQuery.data?.filesTotal ?? files.length,
                  })}
                </Title>
                <SegmentedControl
                  size="xs"
                  color="blue"
                  value={fileStatusFilter ?? 'all'}
                  onChange={value =>
                    setFileStatusFilter(
                      value === 'all' ? null : (value as FileCopyOperationFileStatus),
                    )
                  }
                  data={[
                    { value: 'all', label: t('pages.fileCopy.detail.filterAll') },
                    ...statusFilters.map(filter => ({
                      value: filter,
                      label: t(`pages.fileCopy.fileStatuses.${filter}`),
                    })),
                  ]}
                />
              </Stack>
              <TextInput
                placeholder={t('pages.fileCopy.detail.searchPlaceholder')}
                size="xs"
                value={search}
                onChange={event => setSearch(event.currentTarget.value)}
                style={{ width: 180 }}
              />
            </Group>
            {files.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="md">
                {t('pages.fileCopy.detail.emptyFiltered')}
              </Text>
            ) : (
              <LXTable
                items={fileItems}
                columns={fileColumns}
                verticalSpacing="sm"
                wrapperStyle={{ maxHeight: 480, overflowY: 'auto' }}
              />
            )}
          </Stack>
        </Stack>
      </CardContent>
    </MUICard>
  )
}
